import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { logger } from "../lib/logger";
import type {
  GameState,
  LobbyState,
  PlayerInfo,
  SpectatorInfo,
  LobbySettings,
} from "../types/game";
import type { StateStorage } from "zustand/middleware";

interface GameStore {
  // Session info
  sessionId: string | null;
  roomCode: string | null;
  playerId: string | null;
  playerToken: string | null;
  displayName: string | null;
  isPublic: boolean;

  // Game state (can be either lobby or in-game)
  gameState: GameState | LobbyState | null;

  // Delta sync tracking
  lastSeenSequence: number;

  // Spectators and settings
  spectators: SpectatorInfo[];
  lobbySettings: LobbySettings | null;
  allowSpectators: boolean;

  // Error state
  sessionError: string | null;
  showSessionErrorModal: boolean;

  // Actions
  setSession: (
    sessionId: string,
    roomCode: string,
    playerId: string,
    playerToken: string,
    displayName: string,
  ) => void;
  setSessionId: (sessionId: string) => void;
  setRoomCode: (roomCode: string) => void;
  setPlayerId: (playerId: string) => void;
  setPlayerToken: (playerToken: string) => void;
  setDisplayName: (displayName: string) => void;
  updateGameState: (state: GameState | LobbyState, sequence?: number) => void;
  updateSequence: (sequence: number) => void;
  getLastSeenSequence: () => number;
  clearSession: () => void;
  setSessionError: (error: string) => void;
  clearSessionError: () => void;
  setRoomPrivacy: (isPublic: boolean) => void;
  toggleRoomPrivacy: () => void;

  // Player actions (atomic updates to avoid race conditions)
  addPlayer: (player: PlayerInfo) => void;
  removePlayer: (playerId: string) => void;
  updatePlayerProfile: (
    playerId: string,
    profile: { profile_emoji?: string | null; theme_primary_color?: string | null },
  ) => void;
  reorderPlayersForHost: (newHostId: string, isCurrentPlayer: boolean) => void;

  // Spectator actions
  addSpectator: (spectator: SpectatorInfo) => void;
  removeSpectator: (playerId: string) => void;
  setSpectators: (spectators: SpectatorInfo[]) => void;

  // Lobby settings actions
  updateLobbySettings: (
    settings: LobbySettings,
    changes?: Record<
      string,
      { old: string | number | boolean; new: string | number | boolean }
    >,
  ) => void;
  setAllowSpectators: (allow: boolean) => void;

  // Selectors
  isInLobby: () => boolean;
  isInGame: () => boolean;
  getCurrentPlayer: () => PlayerInfo | null;
}

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const getSessionStorage = () => {
  if (typeof window === "undefined") {
    return noopStorage;
  }
  try {
    return window.sessionStorage;
  } catch {
    return noopStorage;
  }
};

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: null,
      roomCode: null,
      playerId: null,
      playerToken: null,
      displayName: null,
      isPublic: true,
      gameState: null,
      lastSeenSequence: 0,
      spectators: [],
      lobbySettings: null,
      allowSpectators: true,
      sessionError: null,
      showSessionErrorModal: false,

      // Actions
      setSession: (sessionId, roomCode, playerId, playerToken, displayName) => {
        logger.info("session_set", {
          sessionId,
          roomCode,
          playerId,
          displayName,
        });
        set((prev) => {
          const updates: Partial<GameStore> = {
            sessionId,
            roomCode,
            playerId,
            playerToken,
            displayName,
          };
          if (prev.sessionId && prev.sessionId !== sessionId) {
            updates.gameState = null;
          }
          return { ...prev, ...updates };
        });
        logger.debug("session_state_after_set", { state: get() });
      },

      setSessionId: (sessionId: string) => {
        logger.info("session_id_set", { sessionId });
        set({ sessionId });
      },

      setRoomCode: (roomCode: string) => {
        logger.info("room_code_set", { roomCode });
        set({ roomCode });
      },

      setPlayerId: (playerId: string) => {
        logger.info("player_id_set", { playerId });
        set({ playerId });
      },

      setPlayerToken: (playerToken: string) => {
        logger.info("player_token_set", { hasToken: !!playerToken });
        set({ playerToken });
      },

      setDisplayName: (displayName: string) => {
        logger.info("display_name_set", { displayName });
        set({ displayName });
      },

      updateGameState: (state, sequence) => {
        // Update sequence if provided
        if (sequence !== undefined && sequence > get().lastSeenSequence) {
          set({ lastSeenSequence: sequence });
        }

        const normalizeState = (
          incoming: GameState | LobbyState,
        ): GameState | LobbyState => {
          if (
            incoming &&
            typeof incoming === "object" &&
            "game_state" in incoming &&
            incoming.game_state &&
            typeof incoming.game_state === "object"
          ) {
            const combined: Record<string, unknown> = {
              ...(incoming as unknown as Record<string, unknown>),
              ...(incoming.game_state as unknown as Record<string, unknown>),
            };
            delete combined.game_state;
            return combined as unknown as GameState | LobbyState;
          }
          return incoming;
        };

        const normalizedState = normalizeState(state);
        logger.debug("updateGameState called", { incoming: normalizedState });

        set((current) => {
          const previous = current.gameState;
          logger.debug("updateGameState inside set", { previous });

          if (!previous) {
            const initialState = {
              ...(normalizedState as unknown as Record<string, unknown>),
            };
            if (
              !("phase" in initialState) &&
              typeof initialState.room_status === "string"
            ) {
              initialState.phase = initialState.room_status;
            }
            logger.debug("updateGameState setting initial state", {
              initialState,
            });
            return {
              gameState: initialState as unknown as GameState | LobbyState,
            };
          }

          const merged = {
            ...previous,
            ...normalizedState,
          } as GameState | LobbyState;

          // Preserve critical fields if the update omits them (common for broadcast pings)
          const mergedRecord = merged as unknown as Record<string, unknown>;
          const previousRecord = previous as unknown as Record<string, unknown>;
          const stateRecord = normalizedState as unknown as Record<
            string,
            unknown
          >;

          const preserveKeys = [
            "phase",
            "player_stats",
            "current_round_state",
            "available_cards",
            "players",
            "current_players",
            "all_players",
            "config",
            // Critical game state fields that must be preserved on partial updates
            "current_judge",
            "is_judge",
            "is_host",
            "your_hand",
            "your_player_id",
            "active_player_id",
            "expected_action",
            "current_round",
            "game_id",
            "room_status",
            // Personalized hand fields from player_updates (sent separately from broadcast)
            "my_hand",
            "my_judge_hand",
            "is_my_turn",
            // Character selection state (Terrible Psychiatrist)
            "characters",
            "selected_characters",
            "my_character_id",
            "all_characters_selected",
          ];

          for (const key of preserveKeys) {
            if (!(key in stateRecord) && key in previousRecord) {
              mergedRecord[key] = previousRecord[key];
            }
          }

          // Debug logging for is_my_turn
          if ("is_my_turn" in stateRecord || "is_my_turn" in previousRecord) {
            logger.info("is_my_turn_tracking", {
              incoming: stateRecord.is_my_turn,
              previous: previousRecord.is_my_turn,
              merged: mergedRecord.is_my_turn,
              inIncoming: "is_my_turn" in stateRecord,
              inPrevious: "is_my_turn" in previousRecord,
            });
          }

          // Recompute is_judge when current_judge_id changes (broadcasts include current_judge_id but not is_judge)
          if ("current_judge_id" in stateRecord) {
            const yourPlayerId =
              mergedRecord.your_player_id ||
              previousRecord.your_player_id ||
              current.playerId;
            mergedRecord.is_judge =
              stateRecord.current_judge_id === yourPlayerId;
          }

          // Fallback: if phase missing but room_status present, use room_status
          if (
            !("phase" in mergedRecord) &&
            typeof mergedRecord.room_status === "string"
          ) {
            mergedRecord.phase = mergedRecord.room_status;
          }

          logger.debug("updateGameState setting merged state", { merged });
          return { gameState: merged };
        });
        logger.debug("game_state_updated", { gameState: get().gameState });
      },

      updateSequence: (sequence: number) => {
        if (sequence > get().lastSeenSequence) {
          set({ lastSeenSequence: sequence });
        }
      },

      getLastSeenSequence: () => get().lastSeenSequence,

      clearSession: () => {
        // Note: We intentionally do NOT clear sessionError or showSessionErrorModal here
        // so that error modals remain visible when session is cleared due to an error.
        // Use clearSessionError() to explicitly dismiss errors.
        set({
          sessionId: null,
          roomCode: null,
          playerId: null,
          playerToken: null,
          displayName: null,
          gameState: null,
          lastSeenSequence: 0,
          spectators: [],
          lobbySettings: null,
          allowSpectators: true,
        });
      },

      setSessionError: (error) => {
        logger.error("session_error", { error });
        set({ sessionError: error, showSessionErrorModal: true });
      },

      clearSessionError: () => {
        set({ sessionError: null, showSessionErrorModal: false });
      },

      setRoomPrivacy: (isPublic) => {
        logger.info("room_privacy_set", { isPublic });
        set({ isPublic });
      },

      toggleRoomPrivacy: () => {
        const { isPublic } = get();
        logger.info("room_privacy_toggle", {
          currentPrivacy: isPublic,
          newPrivacy: !isPublic,
        });
        // This will send a WebSocket message - actual toggle happens via server broadcast
        // The store will be updated when ROOM_PRIVACY_CHANGED is received
      },

      // Player actions (atomic updates to avoid race conditions)
      addPlayer: (player) => {
        set((current) => {
          const gameState = current.gameState;
          if (!gameState) return current;

          const updates: Record<string, unknown> = {};
          const stateRecord = gameState as unknown as Record<string, unknown>;

          // Update current_players if it exists
          if (Array.isArray(stateRecord.current_players)) {
            updates.current_players = [...stateRecord.current_players, player];
          }

          // Update all_players if it exists
          if (Array.isArray(stateRecord.all_players)) {
            updates.all_players = [...stateRecord.all_players, player];
          }

          // Update players array (player IDs) if it exists
          if (Array.isArray(stateRecord.players)) {
            updates.players = [...stateRecord.players, player.player_id];
          }

          // Update player_stats if it exists
          if (stateRecord.player_stats && typeof stateRecord.player_stats === "object") {
            updates.player_stats = {
              ...(stateRecord.player_stats as Record<string, unknown>),
              [player.player_id]: {
                display_name: player.display_name,
                is_connected: true,
                is_npc: (player as unknown as { is_npc?: boolean }).is_npc || false,
              },
            };
          }

          logger.info("player_added", { playerId: player.player_id });
          return {
            gameState: { ...gameState, ...updates } as GameState | LobbyState,
          };
        });
      },

      removePlayer: (playerId) => {
        set((current) => {
          const gameState = current.gameState;
          if (!gameState) return current;

          const updates: Record<string, unknown> = {};
          const stateRecord = gameState as unknown as Record<string, unknown>;

          // Update current_players if it exists
          if (Array.isArray(stateRecord.current_players)) {
            updates.current_players = (
              stateRecord.current_players as PlayerInfo[]
            ).filter((p) => p.player_id !== playerId);
          }

          // Update all_players if it exists
          if (Array.isArray(stateRecord.all_players)) {
            updates.all_players = (stateRecord.all_players as PlayerInfo[]).filter(
              (p) => p.player_id !== playerId,
            );
          }

          // Update players array (player IDs) if it exists
          if (Array.isArray(stateRecord.players)) {
            updates.players = (stateRecord.players as string[]).filter(
              (pid) => pid !== playerId,
            );
          }

          logger.info("player_removed", { playerId });
          return {
            gameState: { ...gameState, ...updates } as GameState | LobbyState,
          };
        });
      },

      updatePlayerProfile: (playerId, profile) => {
        set((current) => {
          const gameState = current.gameState;
          if (!gameState) return current;

          const updates: Record<string, unknown> = {};
          const stateRecord = gameState as unknown as Record<string, unknown>;

          // Update in current_players (lobby)
          if (Array.isArray(stateRecord.current_players)) {
            updates.current_players = (stateRecord.current_players as PlayerInfo[]).map(
              (p) =>
                p.player_id === playerId
                  ? { ...p, ...profile }
                  : p,
            );
          }

          // Update in all_players (game)
          if (Array.isArray(stateRecord.all_players)) {
            updates.all_players = (stateRecord.all_players as PlayerInfo[]).map((p) =>
              p.player_id === playerId
                ? { ...p, ...profile }
                : p,
            );
          }

          if (Object.keys(updates).length > 0) {
            logger.info("player_profile_updated", { playerId, profile });
            return {
              gameState: { ...gameState, ...updates } as GameState | LobbyState,
            };
          }
          return current;
        });
      },

      reorderPlayersForHost: (newHostId, isCurrentPlayer) => {
        set((current) => {
          const gameState = current.gameState;
          if (!gameState) return current;

          const updates: Record<string, unknown> = { is_host: isCurrentPlayer };
          const stateRecord = gameState as unknown as Record<string, unknown>;

          // Reorder all_players (game state) to put new host first
          if (Array.isArray(stateRecord.all_players)) {
            const reorderedPlayers = [...(stateRecord.all_players as PlayerInfo[])];
            const newHostIndex = reorderedPlayers.findIndex(
              (p) => p.player_id === newHostId,
            );
            if (newHostIndex > 0) {
              const [newHost] = reorderedPlayers.splice(newHostIndex, 1);
              reorderedPlayers.unshift(newHost);
            }
            updates.all_players = reorderedPlayers;
          }

          // Also update current_players (lobby state) to put new host first
          if (Array.isArray(stateRecord.current_players)) {
            const reorderedLobbyPlayers = [
              ...(stateRecord.current_players as PlayerInfo[]),
            ];
            const newHostIndex = reorderedLobbyPlayers.findIndex(
              (p) => p.player_id === newHostId,
            );
            if (newHostIndex > 0) {
              const [newHost] = reorderedLobbyPlayers.splice(newHostIndex, 1);
              reorderedLobbyPlayers.unshift(newHost);
            }
            updates.current_players = reorderedLobbyPlayers;
          }

          logger.info("players_reordered_for_host", { newHostId, isCurrentPlayer });
          return {
            gameState: { ...gameState, ...updates } as GameState | LobbyState,
          };
        });
      },

      // Spectator actions
      addSpectator: (spectator) => {
        set((current) => ({
          spectators: [...current.spectators, spectator],
        }));
        logger.info("spectator_added", { spectator });
      },

      removeSpectator: (playerId) => {
        const { spectators } = get();
        logger.info("spectator_removed", { playerId });
        set({ spectators: spectators.filter((s) => s.player_id !== playerId) });
      },

      setSpectators: (spectators) => {
        logger.info("spectators_set", { count: spectators.length });
        set({ spectators });
      },

      // Lobby settings actions
      updateLobbySettings: (settings, changes) => {
        logger.info("lobby_settings_updated", { settings, changes });
        set({ lobbySettings: settings });
      },

      setAllowSpectators: (allow) => {
        logger.info("allow_spectators_set", { allow });
        set({ allowSpectators: allow });
      },

      // Selectors
      isInLobby: () => {
        const state = get().gameState;
        return state?.room_status === "lobby";
      },

      isInGame: () => {
        const state = get().gameState;
        return state !== null && state.room_status !== "lobby";
      },

      getCurrentPlayer: () => {
        const { gameState, playerId } = get();
        if (!gameState || !playerId) return null;

        if ("all_players" in gameState) {
          // In-game state
          return (
            gameState.all_players.find((p) => p.player_id === playerId) || null
          );
        } else if ("current_players" in gameState) {
          // Lobby state
          return (
            gameState.current_players.find((p) => p.player_id === playerId) ||
            null
          );
        }

        return null;
      },
    }),
    {
      name: "responsum-game-storage",
      storage: createJSONStorage(getSessionStorage),
      partialize: (state) => ({
        sessionId: state.sessionId,
        roomCode: state.roomCode,
        playerId: state.playerId,
        playerToken: state.playerToken,
        displayName: state.displayName,
      }),
    },
  ),
);

// Expose store on window for E2E testing (development only)
if (typeof window !== "undefined") {
  const globalWindow = window as unknown as {
    useGameStore?: typeof useGameStore;
    __ZUSTAND_STORE__?: typeof useGameStore;
  };
  globalWindow.useGameStore = useGameStore;
  globalWindow.__ZUSTAND_STORE__ = useGameStore;
}
